import React from 'react'
import { Autocomplete, Chip, CircularProgress, TextField } from '@mui/material'
import type { AlertRecipient } from '../../hooks/useAlerts'

export interface AlertRecipientsFieldProps {
  /** The chosen colleagues' emails. */
  value: string[]
  onChange: (emails: string[]) => void
  /** Who may be named for what has been typed (GET /alerts/:customer/recipients?q=). */
  options: AlertRecipient[]
  loading: boolean
  /** The recipients route's reason it could not answer (a 502 from licensing). */
  error?: string | null
  inputValue: string
  onInputChange: (q: string) => void
  /** Set by DisabledReason when the caller is not the alert's owner. */
  disabled?: boolean
  'aria-describedby'?: string
}

// The server falls back to the email when licensing has no name.
const hasName = (r: AlertRecipient) => Boolean(r.name) && r.name.toLowerCase() !== r.email.toLowerCase()
const label = (r: AlertRecipient) => (hasName(r) ? `${r.name} (${r.email})` : r.email)

/**
 * "Also notify": colleagues who get the alert's bell row with its owner. The
 * server lists who may be named (never the caller, never a Manager for a
 * non-Manager), so the picker filters nothing itself beyond what is chosen.
 */
const AlertRecipientsField: React.FC<AlertRecipientsFieldProps> = ({
  value,
  onChange,
  options,
  loading,
  error,
  inputValue,
  onInputChange,
  disabled,
  'aria-describedby': describedBy,
}) => {
  // A chosen colleague stays a chip when the current search no longer lists them.
  const chosen: AlertRecipient[] = value.map((email) => options.find((o) => o.email === email) ?? { email, name: '' })
  return (
    <Autocomplete
      multiple
      size="small"
      disabled={disabled}
      options={options}
      value={chosen}
      inputValue={inputValue}
      // `chosen` is a new array each render, which MUI reads as a new value
      // and answers with a 'reset' that would wipe what is being typed; the
      // text is cleared here after a pick instead.
      onInputChange={(_e, q, reason) => { if (reason !== 'reset') onInputChange(q) }}
      onChange={(_e, next) => {
        onChange(next.map((r) => r.email))
        onInputChange('')
      }}
      filterOptions={(opts) => opts.filter((o) => !value.includes(o.email))}
      getOptionLabel={label}
      isOptionEqualToValue={(a, b) => a.email === b.email}
      loading={loading}
      noOptionsText={inputValue.trim() ? 'No colleague matches' : 'No colleagues to add'}
      renderTags={(items, getTagProps) =>
        items.map((r, index) => {
          const { key, ...tagProps } = getTagProps({ index })
          return <Chip key={key} size="small" label={hasName(r) ? r.name : r.email} title={r.email} {...tagProps} />
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Also notify"
          placeholder={value.length ? '' : 'Colleagues, by name or email'}
          error={Boolean(error)}
          helperText={error || 'They get the alert in their notifications too.'}
          inputProps={{ ...params.inputProps, ...(describedBy ? { 'aria-describedby': describedBy } : {}) }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default AlertRecipientsField
